import React from 'react';

import { useGraphQLQuery } from '@betterment/graphql';
import { StandardEventProperties } from '@betterment/js-runtime';
import { Box, Card, H2, HStack, Icon, ListRow, Separator, Text } from '@betterment/samba-react';
import { css } from '@betterment/samba-styled-system/css';
import { CardLoader, SambaAutoLink, useFeatureFlag } from '@betterment/shared-components';

import { trackClick } from '../../features/track';
import { addAccountUrl } from '../../Shared/addAccountUrl';
import { urlBuilder } from '../../Shared/urlBuilder';
import { AccountRecommendationModal } from '../account-recommendation-modal/AccountRecommendationModal';
import { AccountRecommendationOptionDocument } from '../account-recommendation-modal/graphql/query.accountRecommendationOption.generated';
import { useProductOnboardingOptions } from '../hooks/useProductOnboardingOptions';

const ActionableRow: React.FC<{
  name: string;
  image: string;
  shortDescription: string;
  eventProperties: StandardEventProperties;
  href: string;
}> = ({ name, image, shortDescription, eventProperties, href }) => {
  const { name: eventName, ...restEventProperties } = eventProperties;
  return (
    <Box className="group">
      <SambaAutoLink
        to={href}
        title={`${name} link`}
        css={{ textDecoration: 'none!important' }}
        onClick={() => trackClick({ name: eventName, ...restEventProperties })}
      >
        <ListRow>
          <ListRow.LeadingAsset css={{ width: '36px' }}>
            <img
              src={image}
              alt={`${name} icon`}
              className={css({
                width: 'icon.medium',
                height: 'icon.medium',
                borderRadius: 'border.medium',
              })}
            />
          </ListRow.LeadingAsset>
          <ListRow.LeadingContent>
            <Box>
              <Text
                variant="text100"
                color="text"
                css={{
                  textDecoration: {
                    _groupHover: 'underline',
                    _groupFocus: 'underline',
                  },
                }}
              >
                {name}
              </Text>
            </Box>
            <Box>
              <Text variant="text50" color="text.weak">
                {shortDescription}
              </Text>
            </Box>
          </ListRow.LeadingContent>
          <ListRow.TrailingAsset>
            <Icon name="CaretRight" size="small" color="icon" />
          </ListRow.TrailingAsset>
        </ListRow>
      </SambaAutoLink>
    </Box>
  );
};

export const ProductOnboardingNotInProgressCard: React.FC = () => {
  const { data } = useProductOnboardingOptions();

  const { enabled: isAdvisorNetworkEnabled, loading: isAdvisorNetworkFlagLoading } = useFeatureFlag({
    splitName: 'retail.advisor_network_q4_2025_enabled',
    context: 'ProductOnboardingNotInProgressCard',
  });

  const { enabled: isAccountRecommendationEnabled } = useFeatureFlag({
    splitName: 'retail.ai_account_recommendation_q1_2026_enabled',
    context: 'ProductOnboardingNotInProgressCard',
  });

  const { data: accountRecommendationOptionData } = useGraphQLQuery(AccountRecommendationOptionDocument, {
    throwOnError: false,
    skip: !isAccountRecommendationEnabled,
  });

  if (!data || isAdvisorNetworkFlagLoading) return <CardLoader />;

  if (!data.notInProgressOptions.length) return null;

  const options = data.notInProgressOptions;
  const hasInProgressOptions = data.inProgressOptions.length > 0;

  const services = options.filter(option => option.__typename === 'PremiumOnboardingOption');
  const products = options.filter(option => option.__typename !== 'PremiumOnboardingOption');
  const { accountRecommendationOption } = accountRecommendationOptionData || {};

  return (
    <>
      <Card data-testid="specProductOnboardingNotInProgressCard">
        <Card.Header>
          <HStack css={{ padding: 'space.300', justifyContent: 'space-between', alignItems: 'center' }}>
            <H2 variant="title200">{hasInProgressOptions ? 'Explore more products' : 'Open your first account'}</H2>
            {accountRecommendationOption && <AccountRecommendationModal compact={true} />}
          </HStack>
        </Card.Header>
        <Card.Content>
          {products.map(option => (
            <React.Fragment key={option.id}>
              <Separator css={{ fullWidthWithInset: 'space.300' }} />
              <ActionableRow
                shortDescription={option.shortDescription}
                image={option.image.light}
                name={option.name}
                eventProperties={{
                  name: option.legacyProductCategory,
                  module: `ProductCatalog.products`,
                  interaction: 'click',
                }}
                href={addAccountUrl({ product_category: option.legacyProductCategory })}
              />
            </React.Fragment>
          ))}
        </Card.Content>
      </Card>
      <Card data-testid="specProductOnboardingNotInProgressCardAdvice">
        <Card.Header css={{ padding: 'space.300' }}>
          <H2 variant="title200">Not sure where to start?</H2>
        </Card.Header>
        <Card.Content>
          {services.map(option => (
            <React.Fragment key={option.id}>
              <Separator css={{ fullWidthWithInset: 'space.300' }} />
              <ActionableRow
                shortDescription={option.shortDescription}
                image={option.image.light}
                name={isAdvisorNetworkEnabled ? 'Work with an advisor' : option.name}
                eventProperties={{
                  name: option.legacyProductCategory,
                  module: `ProductCatalog.services`,
                  interaction: 'click',
                }}
                href={
                  isAdvisorNetworkEnabled
                    ? `${urlBuilder.appAdvisorNetwork.absolute()}/plan_selection`
                    : urlBuilder.appAdviceInitiationFlow.absolute()
                }
              />
            </React.Fragment>
          ))}
        </Card.Content>
      </Card>
    </>
  );
};
